import { useState } from 'react';
import {
  AccountModel,
  ChungTu,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  NgoaiTe,
  NhapXuat,
  QuyenChungTu,
  Tax
} from '@/types/schemas';

export interface FormFieldState {
  // Supplier information
  nhaCungCap: KhachHang | null;
  nhapXuat: NhapXuat | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  donViCoSo: DonViCoSo | null;

  // Tax information
  taxModel: Tax | null;
  taiKhoanThue: AccountModel | null;

  // Other information
  soChungTu: string;

  // Checkbox states
  transfer_yn: boolean;
  outvat_yn: boolean;
  lo_yn: boolean;
  vi_tri_yn: boolean;
  qc_yn: boolean;
}

export interface FormFieldActions {
  // Search field setters
  setNhaCungCap: (nhaCungCap: KhachHang) => void;
  setNhapXuat: (nhapXuat: NhapXuat) => void;
  setTaiKhoan: (taiKhoan: AccountModel) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setDonViCoSo: (donViCoSo: DonViCoSo) => void;
  setTaxModel: (taxModel: Tax) => void;
  setTaiKhoanThue: (taiKhoanThue: AccountModel) => void;

  setSoChungTu: (soChungTu: string) => void;

  // Checkbox setters
  setTransferYn: (value: boolean) => void;
  setOutvatYn: (value: boolean) => void;
  setLoYn: (value: boolean) => void;
  setViTriYn: (value: boolean) => void;
  setQcYn: (value: boolean) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Supplier information
  nhaCungCap: null,
  nhapXuat: null,
  taiKhoan: null,
  hanThanhToan: null,

  // Document information
  quyenChungTu: null,
  donViCoSo: null,

  // Tax information
  taxModel: null,
  taiKhoanThue: null,

  // Other information
  soChungTu: '',

  // Checkbox states
  transfer_yn: false,
  outvat_yn: false,
  lo_yn: false,
  vi_tri_yn: false,
  qc_yn: false
};

function transformInitialData(initialData?: MuaHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Supplier information
    nhaCungCap: initialData.ma_kh_data || null,
    nhapXuat: initialData.ma_nx_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.ma_tt_data || null,

    // Document information
    quyenChungTu: initialData.so_ct_data || null,
    donViCoSo: initialData.unit_id_data || null,

    // Tax information
    taxModel: initialData.ma_thue_data || null,
    taiKhoanThue: initialData.tk_thue_data || null,

    // Other information
    soChungTu: initialData.so_ct || '',

    // Checkbox states
    transfer_yn: initialData.transfer_yn ?? false,
    outvat_yn: initialData.outvat_yn ?? false,
    lo_yn: initialData.lo_yn ?? false,
    vi_tri_yn: initialData.vi_tri_yn ?? false,
    qc_yn: initialData.qc_yn ?? false
  };
}

export function useFormFieldState(initialData?: MuaHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setNhaCungCap: (nhaCungCap: KhachHang) => {
      setState(prev => ({
        ...prev,
        nhaCungCap
      }));
    },

    setNhapXuat: (nhapXuat: NhapXuat) => {
      setState(prev => ({
        ...prev,
        nhapXuat
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setTaxModel: (taxModel: Tax) => {
      setState(prev => ({
        ...prev,
        taxModel
      }));
    },

    setTaiKhoanThue: (taiKhoanThue: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoanThue
      }));
    },

    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    // Checkbox setters
    setTransferYn: (value: boolean) => {
      setState(prev => ({ ...prev, transfer_yn: value }));
    },

    setOutvatYn: (value: boolean) => {
      setState(prev => ({ ...prev, outvat_yn: value }));
    },

    setLoYn: (value: boolean) => {
      setState(prev => ({ ...prev, lo_yn: value }));
    },

    setViTriYn: (value: boolean) => {
      setState(prev => ({ ...prev, vi_tri_yn: value }));
    },

    setQcYn: (value: boolean) => {
      setState(prev => ({ ...prev, qc_yn: value }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
